// 🔧 become_vendor.js
// ✅ Purpose: Public-facing vendor signup and checkout page
// ✅ Features:
// - Loads event vendor categories from /events/{eventId}/settings/voting
// - Shows only paid vendor categories
// - Collects vendor name, email, website, and description
// - Starts Stripe Checkout through createPublicVendorCheckoutSession
// - Uses event-level Stripe Connect payments
// - Shows success/error notifications at the top of the page
// - Fully block-commented for clarity and future devs

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SeoHead from "../../components/seo_head";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const BecomeVendor = () => {

  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [vendorCategories, setVendorCategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [vendorContactEmail, setVendorContactEmail] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadVendorSettings = async () => {
      if (!eventId) return;

      try {
        setLoading(true);

        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const settings = settingsSnap.exists() ? settingsSnap.data() : {};

        const paidCategories = Array.isArray(settings.vendorCategories)
          ? settings.vendorCategories.filter(
              (category) =>
                category.paymentRequired === true &&
                Number(category.amountCents || 0) > 0
            )
          : [];

        setVendorCategories(paidCategories);

        if (paidCategories.length > 0) {
          setSelectedCategoryName(paidCategories[0].name || "");
        }
      } catch (err) {
        logger.error("❌ Failed to load vendor signup settings:", {
          value: err,
        });

        setStatus({
          type: "error",
          message: "Failed to load vendor signup options.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadVendorSettings();
  }, [eventId]);

  const selectedCategory = vendorCategories.find(
    (category) => category.name === selectedCategoryName
  );

  const formatMoney = (amountCents = 0) =>
    `$${(Number(amountCents || 0) / 100).toFixed(2)}`;

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (checkingOut) return;

    if (!vendorName.trim()) {
      setStatus({
        type: "error",
        message: "Vendor name is required.",
      });

      return;
    }

    if (!vendorContactEmail.trim()) {
      setStatus({
        type: "error",
        message: "Vendor contact email is required.",
      });

      return;
    }

    if (!selectedCategoryName) {
      setStatus({
        type: "error",
        message: "Please select a vendor category.",
      });

      return;
    }

    try {
      setCheckingOut(true);
      setStatus(null);

      const createPublicVendorCheckoutSession = httpsCallable(
        functions,
        "createPublicVendorCheckoutSession"
      );

      const result = await createPublicVendorCheckoutSession({
        eventId,
        vendorName: vendorName.trim(),
        vendorContactEmail: vendorContactEmail.trim().toLowerCase(),
        vendorCategory: selectedCategoryName,
        description,
        url: url.trim(),
      });

      const checkoutUrl = result?.data?.url;

      if (!checkoutUrl) {
        setStatus({
          type: "error",
          message: "Stripe did not return a checkout link.",
        });

        return;
      }

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.VENDOR_CHECKOUT_STARTED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          commerce_type: "vendor",
          source_page: window.location.pathname,
        },
      });

      window.location.href = checkoutUrl;
    } catch (err) {
      logger.error("❌ Failed to start vendor checkout:", {
        value: err,
      });

      setStatus({
        type: "error",
        message:
          err?.message ||
          "Failed to start vendor checkout. Please try again.",
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const vendorSeo = {
    title: `Become a Vendor | ${eventDoc?.name || "Event"} | Let's Go Vote`,
    description: `Apply to become a vendor at ${
      eventDoc?.name || "this event"
    }. Browse vendor opportunities and reserve your booth online.`,
    path: `/${eventslug}/become_vendor`,
    image:
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-sponsor-management.png",
    noindex: !isEventPublic(eventDoc),
    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: eventDoc?.name || "Event",
        path: `/${eventslug}/event`,
      },
      {
        name: "Vendors",
        path: `/${eventslug}/vendors`,
      },
      {
        name: "Become Vendor",
        path: `/${eventslug}/become_vendor`,
      },
    ],
  };

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading vendor signup..." />;
  }

  const vendorPaymentsEnabled =
    eventDoc?.paymentFeatures?.vendorBooths === true;

  return (
  <>
    <SeoHead {...vendorSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "900px" }}>
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🛍 Become a Vendor</h1>

            <p className="lead text-muted mb-0">
              {eventDoc?.name
                ? `Apply and pay for a vendor spot at ${eventDoc.name}.`
                : "Apply and pay for a vendor spot at this event."}
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {!vendorPaymentsEnabled && (
          <div className="alert alert-warning">
            Vendor booth payments are not currently enabled for this event.
          </div>
        )}

        {vendorPaymentsEnabled && vendorCategories.length === 0 && (
          <div className="alert alert-info">
            No paid vendor categories are currently available.
          </div>
        )}

        {vendorPaymentsEnabled && vendorCategories.length > 0 && (
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white">
              <strong>Vendor Signup</strong>
            </div>

            <div className="card-body">
              <form onSubmit={handleCheckout}>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Vendor Category *
                  </label>

                  <select
                    className="form-select"
                    value={selectedCategoryName}
                    onChange={(e) => setSelectedCategoryName(e.target.value)}
                    required
                  >
                    {vendorCategories.map((category) => (
                      <option key={category.name} value={category.name}>
                        {category.name} - {formatMoney(category.amountCents)}
                      </option>
                    ))}
                  </select>

                  {selectedCategory && (
                    <div className="alert alert-light border mt-3 mb-0">
                      <strong>Amount Due:</strong>{" "}
                      {formatMoney(selectedCategory.amountCents)}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Vendor Name *
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Vendor Contact Email *
                  </label>

                  <input
                    type="email"
                    className="form-control"
                    value={vendorContactEmail}
                    onChange={(e) => setVendorContactEmail(e.target.value)}
                    required
                  />

                  <div className="form-text">
                    This email will receive the Stripe payment receipt and vendor confirmation.
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Website URL
                  </label>

                  <input
                    type="url"
                    className="form-control"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://vendor.com"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Vendor Description
                  </label>

                  <ReactQuill
                    theme="snow"
                    value={description}
                    onChange={setDescription}
                  />
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={checkingOut}
                  >
                    {checkingOut
                      ? "Starting Checkout..."
                      : "💳 Continue to Payment"}
                  </button>

                  <Link
                    to={`/${eventslug}/vendors`}
                    className="btn btn-outline-secondary"
                  >
                    Cancel
                  </Link>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="text-center mt-4">
          <Link
            to={`/${eventslug}/vendors`}
            className="btn btn-outline-primary"
          >
            ← Back to Vendors
          </Link>
        </div>
      </div>
    </section>
  </>
  );
};

export default BecomeVendor;
